import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { mockData } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primaryLinks = [
    { name: 'Accueil', href: '/' },
    { name: 'Web / E-commerce', href: '/web-dev' },
    { name: 'Réalisations', href: '/realisations' }
  ];

  const secondaryLinks = [
    { name: 'À propos', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      // Si on est sur la page portfolio ou autre, retourner à l'accueil d'abord
      if (location.pathname !== '/') {
        navigate('/');
        // Attendre que la navigation soit terminée puis scroller
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // On est déjà sur la page d'accueil, scroller directement
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else if (href.startsWith('/')) {
      navigate(href);
    }
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <img 
                src={mockData.hero.logoNav}
                alt=""
                aria-hidden="true"
                className="h-10 w-auto object-contain"
              />
              <span className="text-white font-bold text-lg tracking-wide">C² Productions</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-6">
              {primaryLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:bg-white/10 rounded-full"
                >
                  {link.name}
                </button>
              ))}
              <div className="h-6 w-px bg-white/20"></div>
              {secondaryLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-gray-300 hover:text-white px-3 py-2 text-xs font-medium transition-colors duration-200 hover:bg-white/5 rounded-md"
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden md:block">
            <button
              onClick={() => scrollToSection('#contact')}
              style={{ backgroundColor: '#f97316' }}
              className="hover:bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95"
            >
              Travailler ensemble
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-400 hover:text-white hover:bg-gray-700 p-2 rounded-md transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-slate-900/95 backdrop-blur-sm rounded-lg mt-2">
              {primaryLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white block px-3 py-2 rounded-md text-base font-semibold transition-colors duration-200 w-full text-left"
                >
                  {link.name}
                </button>
              ))}
              <div className="border-t border-white/10 my-2"></div>
              {secondaryLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-gray-300 hover:text-white block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 w-full text-left"
                >
                  {link.name}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('#contact')}
                style={{ backgroundColor: '#f97316' }}
                className="hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors w-full mt-4"
              >
                Travailler ensemble
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
